import { ProxyServiceConfigurationDc } from "@evergis/api";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useProjectView } from "../../../../ducks/project/hooks";

import { useNotification } from "../../../../hooks/notifications/useNotification";
import { useDialogReset } from "../../../../hooks/useDialogs";
import { useTrigger } from "../../../../hooks/useTrigger";
import { updateProxyService, updateRemoteTileLayer } from "../../../../resources/layers";
import { useDefaultScaleRange } from "../../../../hooks/style/useDefaultScaleRange";
import { ResourceMeta } from "../../../../hooks/useResourceMeta";
import { ProxyServiceType } from "../../AddProxyService/components/types";

type ProxyServiceInput = Omit<ProxyServiceConfigurationDc, "sourceType"> & {
  meta: ResourceMeta;
  serviceType: ProxyServiceType;
  subDomains?: string[];
  minResolution?: number;
  maxResolution?: number;
};

// eslint-disable-next-line max-lines-per-function
export const useUpdateProxyService = ({
  name,
  meta,
  serviceType,
  sourceUrl,
  layers,
  subDomains,
  ...proxyLayerConfig
}: ProxyServiceInput) => {
  const { t } = useTranslation("dialogs");
  const closeDialog = useDialogReset();
  const defaultScaleRange = useDefaultScaleRange();
  const { resolution } = useProjectView();
  const [mutation, { data, loading, error }] = updateProxyService.use();
  const [tmsMutation, { data: tmsData, loading: tmsLoading, error: tmsError }] = updateRemoteTileLayer.use();

  useNotification((!loading && error) || (!tmsLoading && tmsError), {
    error: true,
    title: t("editResource.layerUpdateError"),
    description: `${meta.alias}. ${t("editResource.layerUpdateRetry")}`,
  });

  useNotification((!loading && data) || (!tmsLoading && tmsData), {
    success: true,
    title: t("editResource.layerUpdated"),
    description: meta.alias,
  });

  useTrigger((!loading && data) || (!tmsLoading && tmsData), closeDialog);

  const updateProxyLayer = useCallback(async () => {
    if (serviceType === ProxyServiceType.TMS) {
      await tmsMutation({
        input: {
          name: meta.name,
          alias: meta.alias,
          description: meta.description,
          copyrightText: meta.copyrightText,
          urlFormat: sourceUrl,
          subDomains,
          resolution,
          defaultScaleRange,
        },
      });
    } else {
      await mutation({
        input: {
          name,
          data: {
            name,
            alias: meta.alias,
            description: meta.description,
            sourceType: serviceType,
            sourceUrl,
            layers,
            ...proxyLayerConfig,
          },
          resolution,
          defaultScaleRange,
        },
      });
    }
  }, [
    subDomains,
    serviceType,
    sourceUrl,
    tmsMutation,
    meta.name,
    meta.alias,
    meta.description,
    meta.copyrightText,
    resolution,
    defaultScaleRange,
    mutation,
    name,
    layers,
    proxyLayerConfig,
  ]);

  return { updateProxyLayer, proxyLoading: loading || tmsLoading };
};
